// ============================================
// GENERAL SECTION - Extrait de PropertyForm.jsx
// Étape 1.6: Intégration avec ctx
// Behavior: INCHANGÉ
// ============================================
import React from 'react';
import TaxonomySelector from '../TaxonomySelector';

/**
 * Section Général du formulaire de bien
 * @param {Object} ctx - Contexte partagé contenant formData, handlers, etc.
 */
const GeneralSection = ({ ctx }) => {
  const {
    formData,
    setFormData,
    getFieldsConfig,
    getFieldValue,
    getTypesBienByTransaction,
    getActivitiesByTypeBien,
    renderDynamicField,
    handleInputChange
  } = ctx;

  // Charger la configuration dynamique
  const activiteActuelle = formData.bien_general?.activite_principale;
  const config = getFieldsConfig();
  
  const generalConfig = config?.fullConfig?.onglets?.general;
  
  if (!generalConfig) {
    return (
      <div className="p-8 text-center text-gray-500">
        <i className="bi bi-exclamation-triangle text-4xl mb-3 block"></i>
        <p>Configuration de l'onglet Général non disponible</p>
        <p className="text-xs mt-2">Activite: {activiteActuelle || 'Non définie'}</p>
        <p className="text-xs mt-2">Config type: {config?.configType}</p>
      </div>
    );
  }
  
  // Icônes par section
  const sectionIcons = {
    'identification': 'bi-card-heading',
    'prix_honoraires': 'bi-currency-euro',
    'localisation': 'bi-geo-alt'
  };
  
  // Couleurs par section
  const sectionColors = {
    'identification': { from: 'blue-500', to: 'blue-600', hover: 'blue-500/10' },
    'prix_honoraires': { from: 'emerald-500', to: 'teal-600', hover: 'emerald-500/10' },
    'localisation': { from: 'orange-500', to: 'red-600', hover: 'orange-500/10' }
  };
  
  return (
    <div className="space-y-8">
      {/* Modern info header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/10 border border-white/20 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        <div className="relative p-6 flex items-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-blue-500/25">
            <i className="bi bi-info-circle text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-blue-600 bg-clip-text text-transparent">
              Informations Générales
            </h2>
            <p className="text-gray-600 font-medium">
              Configurez les données principales de votre bien immobilier
            </p>
          </div>
        </div>
      </div>

      {/* Taxonomie - Étape 2 */}
      <div className="relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-purple-50/50"></div>
        <div className="relative p-8">
          {/* Section Header */}
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/25">
              <i className="bi bi-diagram-3 text-lg"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Type de transaction</h3>
              <p className="text-gray-600 text-sm">Définissez la nature de votre bien immobilier commercial</p>
            </div>
          </div>
          
          {/* Taxonomy Selector */}
          <TaxonomySelector
            value={{
              operation: formData.operation,
              objet: formData.objet,
              profil: formData.profil,
              sous_profil: formData.sous_profil,
              cible: formData.cible
            }}
            onChange={(taxonomyData) => {
              // Met à jour formData en une seule fois pour éviter tout bug dans handleInputChange
              setFormData(prev => ({
                ...prev,
                operation: taxonomyData.operation || '',
                objet: taxonomyData.objet || '',
                profil: taxonomyData.profil || '',
                sous_profil: taxonomyData.sous_profil || '',
                cible: taxonomyData.cible || ''
              }));

              // Conserver la logique existante si d'autres effets sont liés à handleInputChange
              handleInputChange('operation', null, taxonomyData.operation);
              handleInputChange('objet', null, taxonomyData.objet);
              handleInputChange('profil', null, taxonomyData.profil);
              handleInputChange('sous_profil', null, taxonomyData.sous_profil);
              handleInputChange('cible', null, taxonomyData.cible);
            }}
            disabled={false}
          />
        </div>
      </div>

      {/* Render dynamic sections from JSON */}
      {Object.entries(generalConfig.sections || {}).map(([sectionKey, section]) => {
        const color = sectionColors[sectionKey] || { from: 'gray-500', to: 'gray-600', hover: 'gray-500/10' };
        const icon = sectionIcons[sectionKey] || 'bi-info-circle';
        
        return (
          <div key={sectionKey} className="group relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl shadow-blue-500/5 hover:shadow-lg transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-purple-50/50"></div>
            <div className="relative p-8">
              {/* Section Header */}
              <div className="flex items-center mb-6">
                <div className={`w-12 h-12 bg-gradient-to-br from-${color.from} to-${color.to} rounded-xl flex items-center justify-center text-white mr-4 shadow-lg shadow-${color.from}/25 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`bi ${icon} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{section.label}</h3>
                  {section.description && (
                    <p className="text-gray-600 text-sm">{section.description}</p>
                  )}
                </div>
              </div>
              
              {/* Section Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(section.champs || []).filter(champ => {
                  // Masquer les champs legacy remplacés par la taxonomie
                  const legacyFields = ['transaction_type', 'type_bien', 'activite_principale', 'category', 'subcategory'];
                  return !legacyFields.includes(champ.id);
                }).map((champ, index) => {
                  const parts = champ.groupe_data.split('.');
                  const value = getFieldValue(formData, champ.groupe_data, champ.type);
                  
                  let champToRender = champ;
                  
                  // FILTRAGE DYNAMIQUE: type_bien
                  if (champ.id === 'type_bien' && champ.type === 'enum') {
                    const transactionType = formData.transaction_type || 'Vente';
                    const typesBienFiltres = getTypesBienByTransaction(transactionType);
                    if (typesBienFiltres.length > 0) {
                      champToRender = { ...champ, options: typesBienFiltres };
                    }
                  }
                  
                  // FILTRAGE DYNAMIQUE: activite_principale
                  if (champ.id === 'activite_principale' && champ.type === 'enum') {
                    const typeBienSelectionne = formData.bien_general?.type_bien;
                    if (typeBienSelectionne) {
                      const activitesFiltrées = getActivitiesByTypeBien(typeBienSelectionne);
                      if (activitesFiltrées.length > 0) {
                        champToRender = { ...champ, options: activitesFiltrées };
                      }
                    }
                  }
                  
                  return (
                    <div key={index} className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {champToRender.label}
                        {champToRender.obligatoire && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {renderDynamicField(champToRender, value, (newValue) => {
                        handleInputChange(parts[0], parts[1], newValue);
                      })}
                      {champToRender.description && (
                        <span className="text-xs text-gray-500">{champToRender.description}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GeneralSection;
